import { Injectable, EventEmitter, NgZone } from "@angular/core";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { ToastrService } from "./toastr.service";
import { ExportOption, ExportType } from "../model/exportOption.model";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";

/**
 * Service to handle exporting data to various formats.
 */
@Injectable({
    providedIn: "root", // Ensures this service is available globally through dependency injection
})
export class ExportService {
    constructor(private toastrService: ToastrService, private _ngZone: NgZone) { }

    private excelColumns: string = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    private defaultChartDescription: string = "An export of the selected chart.";
    private defaultExportAllFileName: string = "Custom Dashboard Export";
    private dataForExportAll: {
        data: any;
        chartTitle: string;
        shortDescription: string;
        longDescription: string;
    }[] = [];

    /**
     * Sanitizes a string to be a safe file name for Windows and macOS.
     * @param name The input string.
     * @returns A sanitized string that complies with file name rules.
     */
    private sanitizeFileName(name: string): string {
        const date = new Date();
        const formattedDate = `${date.getFullYear()}${String(
            date.getMonth() + 1
        ).padStart(2, "0")}${String(date.getDate()).padStart(2, "0")}`; //-${String(date.getHours()).padStart(2, '0')}${String(date.getMinutes()).padStart(2, '0')}${String(date.getSeconds()).padStart(2, '0')}`;

        if (!name || name.trim().length === 0) {
            return `export_${formattedDate}`; // Default to "Untitled" if the name is invalid
        }

        // Remove illegal characters for Windows and macOS
        const illegalChars = /[<>:"\/\\|?*\x00-\x1F\x7F]|[:]/g; // Includes control characters and colon
        let sanitized = name.replace(illegalChars, "");

        // Trim leading and trailing spaces and periods
        sanitized = sanitized.trim().replace(/^\.+|\.+$/g, "");

        // Check for reserved Windows file names
        const reservedNames = /^(CON|PRN|AUX|NUL|COM[1-9]|LPT[1-9])$/i;
        if (reservedNames.test(sanitized)) {
            sanitized = sanitized + "_file";
        }

        // Limit the length to 255 characters (common file name limit)
        if (sanitized.length > 255) {
            sanitized = sanitized.substring(0, 255);
        }

        // Default to "Untitled" if the sanitized name is empty after processing
        if (sanitized.length === 0) {
            return `export_${formattedDate}`;
        }

        return sanitized + "_" + formattedDate;
    }

    /**
     * Sanitizes a string to be a safe Excel sheet name.
     * @param name The input string.
     * @returns A sanitized string that complies with Excel sheet name rules.
     */
    private sanitizeSheetName(name: string): string {
        if (!name || name.trim().length === 0) {
            return "Sheet1"; // Default to "Sheet1" if the name is invalid
        }

        // Remove illegal characters
        const illegalChars = /[\\\/\*\[\]\:\?]/g;
        let sanitized = name.replace(illegalChars, "");

        // Trim leading and trailing whitespace
        sanitized = sanitized.trim();

        // Ensure the name does not start or end with an apostrophe
        if (sanitized.startsWith("'")) {
            sanitized = sanitized.slice(1);
        }
        if (sanitized.endsWith("'")) {
            sanitized = sanitized.slice(0, -1);
        }

        // Truncate to 31 characters (Excel's maximum sheet name length)
        if (sanitized.length > 31) {
            sanitized = sanitized.substring(0, 31);
        }

        // Default to "Sheet1" if the sanitized name is empty after processing
        if (sanitized.length === 0) {
            return "Sheet1";
        }

        return sanitized;
    }

    private groupBy(array: any[], key: string) {

        return array.reduce((result, current) => {

            const groupKey = current[key] || "Unknown";

            if (!result[groupKey]) {
                result[groupKey] = [];
            }

            result[groupKey].push(current);

            return result;

        }, {});
    }

    private buildExportOptions(chart: string, chartType: string): ExportOption {
        const option = new ExportOption([], chart);

        if (typeof chartType === "string" && chartType !== "list") {
            option.types.push(ExportType.PDF);
            option.types.push(ExportType.PNG);
        }

        //all options get these
        option.types.push(ExportType.CSV);
        option.types.push(ExportType.Excel);
        option.types.push(ExportType.CSVRAW);
        option.types.push(ExportType.ExcelRAW);

        return option;
    }

    appUsageExportOptions(chartTitle: string, types: ExportType[]) {

        this.exportOptions =
            this.exportOptions.filter(o => o.chart !== chartTitle);

        const option = new ExportOption(types, chartTitle);
        option.locked = true;
        this.exportOptions.push(option);

        this.exportOptionsUpdateEvent.emit(this.exportOptions);
    }

    exportOptions: ExportOption[] = [];

    exportOptionsUpdateEvent = new EventEmitter<ExportOption[]>();

    /**
     * Upserts export options for a chart.
     * @param chart The chart name.
     * @param chartType The chart type.
     */
    upsertExportOptions(chart: string, chartType: string) {
        const existingOptionIndex = this.exportOptions.findIndex(
            (option) => option.chart === chart
        );

        if (existingOptionIndex > -1) {
            // Update existing option
            const existingOption = this.exportOptions[existingOptionIndex];
            if (existingOption.locked) {
                return;
            }
            this.exportOptions[existingOptionIndex] = this.buildExportOptions(
                chart,
                chartType
            );
        } else {
            // Add new option
            const option = this.buildExportOptions(chart, chartType);
            this.exportOptions.push(option);
        }

        this.exportOptionsUpdateEvent.emit(this.exportOptions);
    }

    removeExportOptions(chart: string) {
        this.exportOptions = this.exportOptions.filter((x) => x.chart !== chart);
        this.exportOptionsUpdateEvent.emit(this.exportOptions);
    }

    exportButtonClickEvent = new EventEmitter<{
        chart: string;
        type: ExportType;
    }>();

    exportButtonClick(chart: string, type: ExportType) {
        this.exportButtonClickEvent.emit({ chart, type });
    }

    exportAllButtonClickEvent = new EventEmitter<{
        charts: string[];
        type: ExportType;
    }>();

    exportAllButtonClick(charts: string[], type: ExportType) {
        this.exportAllButtonClickEvent.emit({ charts, type });
    }

    exportCompleteEvent = new EventEmitter<boolean>();

    exportComplete() {
        this._ngZone.run(() => {
            this.exportCompleteEvent.emit(true);
        });
    }

    /**
     * Places the table description two columns after
     * the main data of the worksheet.
     * @param data
     * @param worksheet
     * @param shortDescription
     * @param longDescription
     */
    addDescriptionToWorksheet(
        data: any,
        worksheet: XLSX.WorkSheet,
        forCSV: boolean,
        shortDescription: string,
        longDescription: string = ""
    ) {
        const dataColumns: number =
            data.length > 0 ? Object.entries(data[0]).length : 0;
        const columnAddress = this.determineTheColumnAddress(dataColumns);
        const headerAddress = columnAddress + "1";
        XLSX.utils.sheet_add_aoa(worksheet, [["Description"]], {
            origin: headerAddress,
        });
        XLSX.utils.sheet_add_aoa(
            worksheet,
            [[this.htmlToPlainText(shortDescription, forCSV)]],
            {
                origin: columnAddress + "2",
            }
        );

        //Disabled, the multiple lines description does not work well
        // if (longDescription) {
        //   XLSX.utils.sheet_add_aoa(
        //     worksheet,
        //     [[this.htmlToPlainText(longDescription, forCSV)]],
        //     {
        //       origin: columnAddress + "4",
        //     }
        //   );
        // }
    }

    /**
     * Converts HTML to plain text, preserving line breaks and basic structure.
     * If forCSV is true, removes commas to avoid breaking CSV format.
     * @param html The HTML string to convert.
     * @param forCSV Whether to remove commas for CSV safety.
     * @returns Plain text string.
     */
    private htmlToPlainText(html: string, forCSV: boolean = false): string {
        if (!html) return "";

        // Create a temporary DOM element to parse HTML
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = html;

        // Helper to recursively extract text with structure
        function extractText(node: Node): string {
            if (node.nodeType === Node.TEXT_NODE) {
                return (node as Text).nodeValue || "";
            }
            if (node.nodeType !== Node.ELEMENT_NODE) {
                return "";
            }
            const el = node as HTMLElement;
            let text = "";

            switch (el.tagName.toLowerCase()) {
                case "br":
                    return "\n";
                case "p":
                case "div":
                    text = Array.from(el.childNodes).map(extractText).join("");
                    return text.trim() ? text + "\n" : "";
                case "li":
                    text = Array.from(el.childNodes).map(extractText).join("");
                    return "- " + text.trim() + "\n";
                case "ul":
                case "ol":
                    return Array.from(el.childNodes).map(extractText).join("");
                case "table":
                    if (el instanceof HTMLTableElement) {
                        return (
                            Array.from(el.rows)
                                .map((row: HTMLTableRowElement) =>
                                    Array.from(row.cells)
                                        .map((cell) => extractText(cell))
                                        .join(" | ")
                                )
                                .join("\n") + "\n"
                        );
                    }
                    return "";
                case "tr":
                    return Array.from(el.childNodes).map(extractText).join(" | ") + "\n";
                case "td":
                case "th":
                    return Array.from(el.childNodes).map(extractText).join("").trim();
                default:
                    return Array.from(el.childNodes).map(extractText).join("");
            }
        }

        let plain = extractText(tempDiv);

        // Replace multiple line breaks with a single one
        plain = plain.replace(/\n{2,}/g, "\n").trim();

        // For CSV, remove commas
        if (forCSV) {
            plain = plain.replace(/,/g, "");
        }

        return plain;
    }

    determineTheColumnAddress(numberOfDataColumns: number) {
        // Please note: this will only work for up to 26 * 26 or 676 columns on the worksheet
        // Adding an extra column between the data and the description
        const descriptionDataColumn = numberOfDataColumns + 1;
        if (descriptionDataColumn < 26) {
            // should only have a single letter column
            return this.excelColumns.charAt(descriptionDataColumn);
        } else {
            // should have a double letter
            const indexOfFirstLetter = Math.floor(descriptionDataColumn / 26) - 1;
            const firstLetterOfAddress = this.excelColumns.charAt(indexOfFirstLetter);
            const indexOfSecondLetter = descriptionDataColumn % 26;
            const secondLetterOfAddress =
                this.excelColumns.charAt(indexOfSecondLetter);
            return "" + firstLetterOfAddress + secondLetterOfAddress;
        }
    }

    exportAllToExcel() {
        try {
            const wb: XLSX.WorkBook = XLSX.utils.book_new();

            let count = 1;
            this.dataForExportAll.forEach(
                ({ data, chartTitle, shortDescription, longDescription }) => {
                    //add primary data sheet
                    let ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(data);
                    XLSX.utils.book_append_sheet(
                        wb,
                        ws,
                        this.sanitizeSheetName(chartTitle)
                    );

                    // Add a unique description sheet
                    const descSheetName = "Description " + count;
                    const descData = [
                        [
                            "Short Description",
                            this.htmlToPlainText(shortDescription, false),
                        ],
                        ["Long Description", this.htmlToPlainText(longDescription, false)],
                    ];
                    const descWs = XLSX.utils.aoa_to_sheet(descData);
                    XLSX.utils.book_append_sheet(wb, descWs, descSheetName);
                    count++;
                }
            );

            const wbout: ArrayBuffer = XLSX.write(wb, {
                bookType: "xlsx",
                type: "array",
            });
            const blob = new Blob([wbout], { type: "application/octet-stream" });

            saveAs(blob, `${this.defaultExportAllFileName}.xlsx`);
            this.toastrService.displayToastrMessage(
                "success",
                "Excel file exported successfully."
            );
            this._ngZone.run(() => {
                this.exportComplete();
            });
        } catch (error) {
            console.error("Error exporting all to Excel:", error);
            this.toastrService.displayToastrMessage(
                "error",
                "Failed to export all to Excel file."
            );
            this._ngZone.run(() => {
                this.exportComplete();
            });
        }
    }

    /**
     *
     * @param data Object
     * @param chartTitle
     * @param shortDescription
     * @param longDescription
     */
    exportToExcel(
        data: any,
        chartTitle: string = "export",
        shortDescription: string = this.defaultChartDescription,
        longDescription: string = ""
    ) {
        try {
            const wb: XLSX.WorkBook = XLSX.utils.book_new();

            // Add primary data sheet
            const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(data);
            XLSX.utils.book_append_sheet(wb, ws, this.sanitizeSheetName(chartTitle));

            // Add a description sheet
            const descSheetName = "Description";
            const descData = [
                ["Short Description", this.htmlToPlainText(shortDescription, false)],
                ["Long Description", this.htmlToPlainText(longDescription, false)],
            ];
            const descWs = XLSX.utils.aoa_to_sheet(descData);
            XLSX.utils.book_append_sheet(wb, descWs, descSheetName);

            const wbout: ArrayBuffer = XLSX.write(wb, {
                bookType: "xlsx",
                type: "array",
            });
            const blob = new Blob([wbout], { type: "application/octet-stream" });

            saveAs(blob, `${this.sanitizeFileName(chartTitle)}.xlsx`);
            this.toastrService.displayToastrMessage(
                "success",
                "Excel file exported successfully."
            );
            this._ngZone.run(() => {
                this.exportComplete();
            });
        } catch (error) {
            console.error("Error exporting to Excel:", error);
            this.toastrService.displayToastrMessage(
                "error",
                "Failed to export Excel file."
            );
            this._ngZone.run(() => {
                this.exportComplete();
            });
        }
    }

    exportAppUsageDataToExcelMultiSheet(
        exportData: {
            summary: any[],
            users: any[],
            sessions: any[],
            details: any[],
            dateRange?: string
        },
        fileName: string = "App Usage Report"
    ) {
        try {
            const wb: XLSX.WorkBook = XLSX.utils.book_new();

            const createStyledSheet = (data: any[], sheetName: string) => {

                // Create empty sheet first
                const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet([]);

                // Add filter date range note at top
                if (exportData.dateRange) {
                    XLSX.utils.sheet_add_aoa(
                        ws,
                        [
                            [`Filtered Date Range: ${exportData.dateRange}`],
                            []
                        ],
                        { origin: "A1" }
                    );
                }

                if (!data || data.length === 0) {

                    XLSX.utils.sheet_add_aoa(
                        ws,
                        [["No data available"]],
                        { origin: "A3" }
                    );

                    XLSX.utils.book_append_sheet(wb, ws, sheetName);

                    return;
                }

                // Add JSON data starting from row 3
                XLSX.utils.sheet_add_json(
                    ws,
                    data,
                    {
                        origin: "A3"
                    }
                );

                const headers = Object.keys(data[0]);

                // Auto filter
                ws["!autofilter"] = {
                    ref: XLSX.utils.encode_range({
                        s: { r: 2, c: 0 },
                        e: { r: data.length + 2, c: headers.length - 1 }
                    })
                };

                // Auto column width calculation
                ws["!cols"] = headers.map((header) => {

                    const maxLength = Math.max(
                        header.length,
                        ...data.map(row =>
                            row[header] !== undefined && row[header] !== null
                                ? String(row[header]).length
                                : 0
                        ),
                        25 // ensure enough width for duration strings like "12 hr 45 min"
                    );

                    return { wch: maxLength + 2 };

                });

                XLSX.utils.book_append_sheet(wb, ws, sheetName);

            };

            // Create Sheets
            createStyledSheet(exportData.summary, "Summary");
            createStyledSheet(exportData.users, "Users");
            createStyledSheet(exportData.sessions, "Sessions");
            createStyledSheet(exportData.details, "Session Details");

            const wbout: ArrayBuffer = XLSX.write(
                wb,
                {
                    bookType: "xlsx",
                    type: "array",
                    cellStyles: true
                }
            );

            const blob = new Blob(
                [wbout],
                {
                    type: "application/octet-stream"
                }
            );

            saveAs(
                blob,
                `${this.sanitizeFileName(fileName)}.xlsx`
            );

            this.toastrService.displayToastrMessage(
                "success",
                "Structured Excel export created successfully."
            );

            this._ngZone.run(() => {
                this.exportComplete();
            });

        }
        catch (error) {

            console.error(
                "Error exporting structured Excel:",
                error
            );

            this.toastrService.displayToastrMessage(
                "error",
                "Failed to export structured Excel file."
            );

            this._ngZone.run(() => {
                this.exportComplete();
            });

        }
    }

    exportAppUsageToPDF(
        data: {
            title: string;
            dateRange: string;
            generatedOn: string;
            summary: any[];
            permissionGroups: any[];
        },
        chartTitle: string = "App Usage Report"
    ) {
        try {

            const fileName = this.sanitizeFileName(chartTitle);

            const pdf = new jsPDF({
                orientation: "portrait",
                unit: "pt",
                format: "letter",
                compress: true
            });

            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();

            let y = 40;

            // Header Section
            pdf.setFontSize(18);
            pdf.setFont("helvetica", "bold");
            pdf.text(chartTitle, 40, y);

            y += 25;

            pdf.setFontSize(10);
            pdf.setFont("helvetica", "normal");

            if (data?.dateRange) {
                pdf.text(`Date Range: ${data.dateRange}`, 40, y);
                y += 15;
            }

            if (data?.generatedOn) {
                pdf.text(`Generated On: ${data.generatedOn}`, 40, y);
                y += 15;
            }

            y += 10;

            // Summary Section
            pdf.setFontSize(14);
            pdf.setFont("helvetica", "bold");
            pdf.text("Summary", 40, y);
            y += 20;

            if (data.summary?.length > 0) {

                const headers = Object.keys(data.summary[0]);

                autoTable(pdf, {
                    startY: y,
                    head: [headers],
                    body: data.summary.map(row => headers.map(h => row[h])),
                    theme: "grid",
                    margin: { left: 40, right: 40 },
                    pageBreak: "auto",
                    rowPageBreak: "avoid",

                    styles: {
                        fontSize: 9,
                        overflow: "linebreak"
                    },

                    headStyles: { fillColor: [41, 128, 185] }
                });

                y = (pdf as any).lastAutoTable.finalY + 20;
            }

            // Permission Groups Section
            data.permissionGroups?.forEach(permissionGroup => {

                if (y > pageHeight - 60) {
                    pdf.addPage();
                    y = 40;
                }

                pdf.setFontSize(13);
                pdf.setFont("helvetica", "bold");
                pdf.text(`Permission Type: ${permissionGroup.permissionType}`, 40, y);
                y += 15;

                if (!permissionGroup.users?.length) {

                    pdf.setFontSize(10);
                    pdf.setFont("helvetica", "italic");
                    pdf.text("No users found.", 60, y);
                    y += 20;

                    return;
                }

                const userTableBody = permissionGroup.users.map(user => [
                    user.name,
                    user.email,
                    user.totalSessions,
                    user.totalTime
                ]);

                autoTable(pdf, {
                    startY: y,
                    head: [["Name", "Email", "Total Sessions", "Total Time"]],
                    body: userTableBody,
                    theme: "grid",
                    margin: { left: 60, right: 40 },

                    pageBreak: "auto",
                    rowPageBreak: "avoid",

                    styles: {
                        fontSize: 9,
                        overflow: "linebreak"
                    }
                });

                y = (pdf as any).lastAutoTable.finalY + 10;

                permissionGroup.users.forEach(user => {

                    if (y > pageHeight - 60) {
                        pdf.addPage();
                        y = 40;
                    }

                    pdf.setFontSize(11);
                    pdf.setFont("helvetica", "bold");
                    pdf.text(`User: ${user.name}`, 60, y);
                    y += 15;

                    if (!user.sessions?.length) {
                        pdf.setFontSize(10);
                        pdf.setFont("helvetica", "italic");
                        pdf.text("No sessions found.", 80, y);
                        y += 15;
                        return;
                    }

                    const sessionBody = user.sessions.map(session => [
                        session.sessionId,
                        session.loginTime,
                        session.duration,
                        session.manualsCount
                    ]);

                    autoTable(pdf, {
                        startY: y,
                        head: [["Session ID", "Login Time", "Duration", "Manuals"]],
                        body: sessionBody,
                        theme: "grid",
                        margin: { left: 80, right: 40 },

                        // ? FIX
                        pageBreak: "auto",
                        rowPageBreak: "avoid",

                        styles: {
                            fontSize: 9,
                            overflow: "linebreak"
                        }
                    });

                    y = (pdf as any).lastAutoTable.finalY + 10;

                    // Manuals grouped per session
                    user.sessions.forEach(session => {

                        if (!session.manuals || session.manuals.length === 0) {
                            return;
                        }

                        if (y > pageHeight - 80) {
                            pdf.addPage();
                            y = 40;
                        }

                        pdf.setFontSize(10);
                        pdf.setFont("helvetica", "bold");
                        pdf.text(`Manuals opened in Session: ${session.sessionId}`, 100, y);

                        y += 12;

                        const manualHeaders = [
                            "Manual Name",
                            "Pages Viewed",
                            "Search Terms"
                        ];

                        const manualBody = session.manuals.map(manual => [
                            manual.manualName || "",
                            manual.pagesViewed || "",
                            manual.searchTerms || ""
                        ]);

                        autoTable(pdf, {
                            startY: y,
                            head: [manualHeaders],
                            body: manualBody,
                            theme: "grid",
                            margin: { left: 100, right: 40 },

                            // ? FIX
                            pageBreak: "auto",
                            rowPageBreak: "avoid",

                            styles: {
                                fontSize: 8,
                                overflow: "linebreak"
                            },

                            headStyles: { fillColor: [200, 200, 200] }
                        });

                        y = (pdf as any).lastAutoTable.finalY + 15;

                    });

                });

            });

            pdf.save(`${fileName}.pdf`);

            this.toastrService.displayToastrMessage(
                "success",
                "PDF exported successfully."
            );

            this._ngZone.run(() => {
                this.exportComplete();
            });

        } catch (error) {

            console.error("Error exporting App Usage PDF:", error);

            this.toastrService.displayToastrMessage(
                "error",
                "Failed to export PDF."
            );

            this._ngZone.run(() => {
                this.exportComplete();
            });

        }
    }

    exportToCSV(
        data: any,
        chartTitle: string = "export",
        shortDescription: string = this.defaultChartDescription,
        longDescription: string = ""
    ) {
        try {
            const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(data);
            const wb: XLSX.WorkBook = XLSX.utils.book_new();
            this.addDescriptionToWorksheet(
                data,
                ws,
                true,
                shortDescription,
                longDescription
            );
            XLSX.utils.book_append_sheet(wb, ws, this.sanitizeSheetName(chartTitle));

            const wbout: ArrayBuffer = XLSX.write(wb, {
                bookType: "csv",
                type: "array",
            });
            const blob = new Blob([wbout], { type: "text/csv;charset=utf-8;" });

            saveAs(blob, `${this.sanitizeFileName(chartTitle)}.csv`);
            this.toastrService.displayToastrMessage(
                "success",
                "CSV file exported successfully."
            );
            this._ngZone.run(() => {
                this.exportComplete();
            });
        } catch (error) {
            console.error("Error exporting to CSV:", error);
            this.toastrService.displayToastrMessage(
                "error",
                "Failed to export CSV file."
            );
            this._ngZone.run(() => {
                this.exportComplete();
            });
        }
    }

    private async addPDFPage(
        pdf: jsPDF,
        chartElement: HTMLElement,
        chartTitle: string,
        shortDescription: string,
        longDescription: string,
        pageIndex: number = 1
    ) {
        await html2canvas(chartElement, { scale: 1 })
            .then((canvas) => {
                pdf.setPage(pageIndex);

                const pageWidth = 8.5; // Letter page width in inches
                const pageHeight = 11; // Letter page height in inches
                const margin = 1; // 1 inch margin

                // Title
                pdf.setFontSize(16);
                pdf.text(chartTitle, margin, margin - 0.2);

                // Short description
                pdf.setFontSize(12);
                pdf.text(shortDescription, margin, margin + 0.5, { maxWidth: 6 });

                // Long Description (HTML to plain text)
                let y = margin + 0.7;
                let descHeight = 0;
                if (longDescription) {
                    pdf.setFontSize(10);
                    const plainLongDesc = this.htmlToPlainText(longDescription, false);
                    const splitDesc = pdf.splitTextToSize(
                        plainLongDesc,
                        pageWidth - 2 * margin
                    );
                    descHeight = splitDesc.length * 0.18; // Approximate line height in inches
                    // If the description would push the image off the page, limit its height
                    const maxDescHeight = pageHeight - margin - 1 - 0.5; // leave space for image
                    let descToPrint = splitDesc;
                    if (descHeight > maxDescHeight) {
                        const maxLines = Math.floor(maxDescHeight / 0.18);
                        descToPrint = splitDesc.slice(0, maxLines);
                        descHeight = maxLines * 0.18;
                    }
                    pdf.text(descToPrint, margin, y, {
                        maxWidth: pageWidth - 2 * margin,
                    });
                    y += descHeight + 0.2;
                }

                // Image
                const imageData = canvas.toDataURL("image/png");
                const imgWidth = pageWidth - 2 * margin;
                const imgHeight = (canvas.height * imgWidth) / canvas.width;

                // If the image would go off the page, move it to the bottom
                let imgY = y;
                if (imgY + imgHeight > pageHeight - margin) {
                    imgY = pageHeight - margin - imgHeight;
                    if (imgY < y) imgY = y; // never overlap the description above
                }

                pdf.addImage(imageData, "PNG", margin, imgY, imgWidth, imgHeight);
            })
            .catch((error) => {
                console.error("Error capturing chart for PDF export:", error);
                this.toastrService.displayToastrMessage(
                    "error",
                    "Failed to export PDF."
                );
                this._ngZone.run(() => {
                    this.exportComplete();
                });
            });
    }

    async exportAllToPDF() {
        try {
            const pdf = new jsPDF({
                orientation: "portrait",
                unit: "in",
                format: "letter", // 8.5 x 11 inches
                compress: true,
            });

            for (let i = 1; i < this.dataForExportAll.length; i++) {
                pdf.addPage();
            }

            const promises = this.dataForExportAll.map(async (ed, index) => {
                await this.addPDFPage(
                    pdf,
                    ed.data,
                    ed.chartTitle,
                    ed.shortDescription,
                    ed.longDescription,
                    index + 1
                );
            });

            await Promise.all(promises);

            pdf.save(`${this.defaultExportAllFileName}.pdf`);
            this.toastrService.displayToastrMessage(
                "success",
                "PDF exported successfully."
            );
            this._ngZone.run(() => {
                this.exportComplete();
            });
        } catch (error) {
            console.error("Error exporting all to PDF:", error);
            this.toastrService.displayToastrMessage(
                "error",
                "Failed to export all to PDF."
            );
            this._ngZone.run(() => {
                this.exportComplete();
            });
        }
    }

    async exportToPDF(
        chartElement: HTMLElement,
        chartTitle: string = "Chart Export",
        shortDescription: string = this.defaultChartDescription,
        longDescription: string = ""
    ) {
        try {
            const fileName = this.sanitizeFileName(chartTitle);

            const pdf = new jsPDF({
                orientation: "portrait",
                unit: "in",
                format: "letter", // 8.5 x 11 inches
                compress: true,
            });

            await this.addPDFPage(
                pdf,
                chartElement,
                chartTitle,
                shortDescription,
                longDescription
            );

            pdf.save(`${fileName}.pdf`);
            this.toastrService.displayToastrMessage(
                "success",
                "PDF exported successfully."
            );
            this._ngZone.run(() => {
                this.exportComplete();
            });
        } catch (error) {
            console.error("Error exporting to PDF:", error);
            this.toastrService.displayToastrMessage("error", "Failed to export PDF.");
            this._ngZone.run(() => {
                this.exportComplete();
            });
        }
    }

    exportToPNG(
        chartElement: HTMLElement,
        chartTitle: string = "Chart Export",
        shortDescription: string = this.defaultChartDescription,
        longDescription: string = ""
    ) {
        try {
            const fileName = this.sanitizeFileName(chartTitle);

            // Use html2canvas to capture the chart as an image
            html2canvas(chartElement, { scale: 2 })
                .then((canvas) => {
                    // Create a new canvas with the same dimensions as
                    // the existing one, but add more height for the
                    // title and description.
                    const newCanvas = document.createElement("canvas");
                    newCanvas.width = canvas.width;
                    newCanvas.height = canvas.height + 300;

                    // Add the title and description to the canvas
                    const newCtx = newCanvas.getContext("2d");
                    // This white background ensures that the black
                    // text shows up on the image.
                    newCtx.fillStyle = "white";
                    newCtx.fillRect(0, 0, newCanvas.width, newCanvas.height);
                    newCtx.fillStyle = "black";
                    newCtx.textAlign = "center";
                    newCtx.font = "40px 'Open Sans', sans-serif";
                    newCtx.fillText(chartTitle, newCanvas.width / 2, 60, newCanvas.width);
                    newCtx.font = "25px 'Open Sans', sans-serif";
                    newCtx.fillText(
                        shortDescription,
                        newCanvas.width / 2,
                        120,
                        newCanvas.width
                    );
                    newCtx.drawImage(canvas, 0, 250);

                    // TODO: I am here - add long description if it exists

                    // Convert the canvas to a data URL (PNG format)
                    const imageData = newCanvas.toDataURL("image/png");

                    // Convert the data URL to a Blob
                    const byteString = atob(imageData.split(",")[1]);
                    const mimeString = imageData
                        .split(",")[0]
                        .split(":")[1]
                        .split(";")[0];
                    const ab = new ArrayBuffer(byteString.length);
                    const ia = new Uint8Array(ab);
                    for (let i = 0; i < byteString.length; i++) {
                        ia[i] = byteString.charCodeAt(i);
                    }
                    const blob = new Blob([ab], { type: mimeString });

                    // Save the blob as a PNG file
                    saveAs(blob, `${fileName}.png`);

                    this.toastrService.displayToastrMessage(
                        "success",
                        "PNG exported successfully."
                    );
                    this._ngZone.run(() => {
                        this.exportComplete();
                    });
                })
                .catch((error) => {
                    console.error("Error capturing chart for PNG export:", error);
                    this.toastrService.displayToastrMessage(
                        "error",
                        "Failed to export PNG."
                    );
                    this._ngZone.run(() => {
                        this.exportComplete();
                    });
                });
        } catch (error) {
            console.error("Error exporting to PNG:", error);
            this.toastrService.displayToastrMessage("error", "Failed to export PNG.");
            this._ngZone.run(() => {
                this.exportComplete();
            });
        }
    }

    private filterChartsByExportType(
        exportType: ExportType,
        exportOptions: ExportOption[]
    ): string[] {
        return exportOptions
            .filter((eo) => eo.types.includes(exportType))
            ?.map((feo) => feo.chart);
    }

    exportAll(exportOptions: ExportOption[], exportType: ExportType) {
        const filteredChartTitles = this.filterChartsByExportType(
            exportType,
            exportOptions
        );

        this.dataForExportAll = [];

        this.exportAllButtonClick(filteredChartTitles, exportType);

        while (this.dataForExportAll.length < filteredChartTitles.length) {
            // just wait for export processing
        }

        switch (exportType) {
            case ExportType.PDF:
                this.exportAllToPDF();
                break;
            case ExportType.Excel:
                this.exportAllToExcel();
                break;
            default:
                break;
        }
    }

    storeDataForExportAll(
        data: any,
        chartTitle: string = "export",
        shortDescription: string = this.defaultChartDescription,
        longDescription: string = ""
    ) {
        const dataToExport = {
            data: data,
            chartTitle: chartTitle,
            shortDescription: shortDescription,
            longDescription: longDescription,
        };

        this.dataForExportAll.push(dataToExport);
    }
}
